/**
 * Auto-contrast ink picker for the widget.
 *
 * The widget derives its whole palette from two owner-chosen colours
 * (--primary / --accent). Text drawn ON those solid surfaces was hardcoded
 * white, which is unreadable on a light/odd brand colour. This module picks
 * black or white per surface by WCAG relative luminance, so the ink always
 * contrasts with the background.
 *
 * Pure + dependency-free → unit-testable in isolation (contrast-ink.test.ts).
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * Parse a hex colour string into 0-255 channels.
 *  - `#rgb` shorthand expands to `#rrggbb`.
 *  - `#rrggbb` parses directly.
 *  - Anything else (named colours, `rgb(...)`, empty, malformed) → null.
 */
export function parseHex(hex: string): Rgb | null {
  if (typeof hex !== 'string') return null;
  const s = hex.trim();
  const m3 = /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/.exec(s);
  if (m3) {
    return {
      r: parseInt(m3[1] + m3[1], 16),
      g: parseInt(m3[2] + m3[2], 16),
      b: parseInt(m3[3] + m3[3], 16),
    };
  }
  const m6 = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(s);
  if (m6) {
    return {
      r: parseInt(m6[1], 16),
      g: parseInt(m6[2], 16),
      b: parseInt(m6[3], 16),
    };
  }
  return null;
}

/**
 * WCAG 2.x relative luminance (0..1) for an sRGB colour. Linearizes each
 * channel before the weighted sum (per the WCAG sRGB formula).
 */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const lin = (channel: number): number => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/**
 * Pick black or white ink for text drawn on the given background colour.
 *  - Unparseable input → '#ffffff' (matches the widget's historical hardcoded
 *    white, so an odd CSS value never produces invisible text).
 *  - Otherwise pick whichever of black/white yields the HIGHER WCAG contrast
 *    ratio against the background. This crossover sits where the two contrast
 *    ratios are equal (luminance ≈ 0.179), NOT at luminance 0.5 — so mid-tone
 *    brand accents like #E8593C (L≈0.25) correctly get black ink, which a naive
 *    >0.5 test wrongly painted white (and made unreadable).
 */
export function inkFor(hex: string): '#000000' | '#ffffff' {
  const rgb = parseHex(hex);
  if (rgb === null) return '#ffffff';
  const L = relativeLuminance(rgb);
  const contrastBlack = (L + 0.05) / 0.05;
  const contrastWhite = 1.05 / (L + 0.05);
  return contrastBlack >= contrastWhite ? '#000000' : '#ffffff';
}

/**
 * Ink (text colour) for a themed surface: the owner's EXPLICIT value wins when
 * it's a valid hex, otherwise auto-contrast over the base colour (inkFor flips
 * black/white by luminance). Single source of the rule — used by applyTheme()
 * (--primary-ink / --accent-ink) and the search-box accent publish; '' when
 * neither input is usable (caller skips setting the CSS var).
 */
export function resolveInk(explicit: string | null | undefined, base: string | null | undefined): string {
  if (parseHex(explicit ?? '')) return explicit as string;
  return base ? inkFor(base) : '';
}
